import { Fragment, type ReactNode } from 'react';

import {
    ENTITY_CONFIG,
    ENTITY_PILL_CLASSES,
} from '@apps/web/components/phoebe_chat/entity_visuals';
import { START_OUTREACH_MAX_SHIFT_COUNT } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import { dynamicWidgetBindingProps } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_dom';
import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import { type DynamicWidgetMetricTags } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import {
    type ShiftListWidgetModel,
    type ShiftListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { useDynamicWidgetPromptAction } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widgets_context';
import {
    actionButtonDisabled,
    actionButtonLabel,
    getActionIssue,
    shiftReferences,
    type ShiftRowViewModel,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_view_model';
import {
    TEMPLATE_CARD_ROW_CLASS,
    TEMPLATE_OUTLINE_BUTTON_XS_CLASS,
    TEMPLATE_TOKEN_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { type RenderedShiftActionState } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/use_shift_widget_actions';
import { Badge } from '@apps/web/components/ui/badge';
import { Spinner } from '@apps/web/components/ui/spinner';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import { cn } from '@apps/web/lib/utils';

const ClientIcon = ENTITY_CONFIG.client.icon;

function shiftRowMetaLine(
    row: ShiftListWidgetRow,
    widgetId: string,
    rowIndex: number,
    onCaregiverClick?: (caregiverId: string) => void,
): ReactNode {
    const parts: Array<{ readonly key: string; readonly node: ReactNode }> = [];
    if (row.timeLabel) {
        parts.push({ key: 'time', node: row.timeLabel.toUpperCase() });
    }
    if (row.city) {
        parts.push({ key: 'city', node: row.city });
    }
    const isRecurring = row.frequency !== null && row.frequency !== 'once';
    if (isRecurring) {
        parts.push({ key: 'frequency', node: `ongoing ${row.frequency}` });
    }
    if (isRecurring && row.seriesOpen !== null && row.seriesCovered !== null) {
        parts.push({
            key: 'series',
            node:
                `${row.seriesOpen} of ` +
                `${row.seriesOpen + row.seriesCovered} upcoming shifts open`,
        });
    }
    const caregiverId = row.caregiverId;
    if (row.caregiverName) {
        parts.push({
            key: 'caregiver',
            node:
                onCaregiverClick && caregiverId ? (
                    <button
                        type="button"
                        className="hover:text-foreground cursor-pointer underline-offset-2 hover:underline"
                        onClick={() => onCaregiverClick(caregiverId)}
                        {...dynamicWidgetBindingProps(
                            widgetId,
                            `rows.${rowIndex}.caregiverName`,
                        )}
                    >
                        {row.caregiverName}
                    </button>
                ) : (
                    row.caregiverName
                ),
        });
    }
    if (row.roleRequired) {
        parts.push({ key: 'role', node: row.roleRequired.toUpperCase() });
    }
    row.skillsRequired.forEach((skill) => {
        parts.push({ key: `skill:${skill}`, node: skill });
    });
    return parts.map((part, partIndex) => (
        <Fragment key={part.key}>
            {partIndex > 0 ? ' · ' : null}
            {part.node}
        </Fragment>
    ));
}

// Mockup fidelity (T4): outreach state replaces the status pill when an
// outreach is running — the mockup never shows "Open" and an outreach pill
// side by side.
function shiftOutreachPill(
    row: ShiftListWidgetRow,
): { readonly label: string; readonly intent: 'info' | 'warning' } | null {
    if (row.outreachState === undefined) {
        return null;
    }
    if (row.outreachState.active) {
        return { label: 'Outreach in progress', intent: 'info' };
    }
    if (row.outreachState.paused) {
        return { label: 'Outreach paused', intent: 'warning' };
    }
    return null;
}

export function ShiftRow({
    model,
    view,
    actionState,
    metricTags,
    onCaregiverClick,
    actionsEnabled,
    onViewOutreach,
    onRequestOutreach,
}: {
    readonly model: ShiftListWidgetModel;
    readonly view: ShiftRowViewModel;
    readonly actionState: RenderedShiftActionState;
    readonly metricTags: DynamicWidgetMetricTags;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly actionsEnabled: boolean;
    readonly onViewOutreach?: (
        shiftId: string,
    ) => Promise<boolean | void> | boolean | void;
    readonly onRequestOutreach: (
        rows: ReadonlyArray<ShiftListWidgetRow>,
        displayText: string,
    ) => Promise<void>;
}) {
    const { row, rowIndex } = view;
    const promptAction = useDynamicWidgetPromptAction();
    const actionIssue = getActionIssue(model, actionState.state, [
        `rows.${rowIndex}.occurrenceIds`,
    ]);
    const outreachPill = shiftOutreachPill(row);
    const hasRunningOutreach = outreachPill !== null;
    const showNoOutreachToken =
        row.outreachState !== undefined && !hasRunningOutreach;
    const canViewOutreach = hasRunningOutreach && onViewOutreach !== undefined;
    // The baseline keeps both valid next steps on an uncovered shift: start
    // outreach or assign directly. Once outreach is running, both are replaced
    // by the single "View outreach" action.
    const canStartOutreach =
        !hasRunningOutreach &&
        view.isOpenShift &&
        actionsEnabled &&
        row.occurrenceIds.length > 0;
    const canAssign =
        canStartOutreach && row.status === 'open' && promptAction !== null;
    const canRequestOutreach =
        canStartOutreach &&
        row.occurrenceIds.length <= START_OUTREACH_MAX_SHIFT_COUNT;

    return (
        <div className={TEMPLATE_CARD_ROW_CLASS}>
            {/* The flex basis keeps the client name and meta line readable in
                the narrow chat sidebar: instead of the badge/action group
                squeezing this block to a few characters, the group wraps
                below the name as a whole. */}
            <div className="min-w-0 flex-[1_1_14rem]">
                <div className="flex items-center gap-1.5 font-medium">
                    <span
                        className={cn(
                            'inline-flex max-w-full min-w-0 items-center gap-0.5 rounded px-1 align-middle text-[13px] font-medium whitespace-nowrap',
                            ENTITY_PILL_CLASSES.client,
                        )}
                    >
                        <ClientIcon
                            className="size-3.5 shrink-0"
                            aria-hidden="true"
                        />
                        <span
                            className="min-w-0 truncate"
                            {...dynamicWidgetBindingProps(
                                model.widgetId,
                                `rows.${rowIndex}.patientName`,
                            )}
                        >
                            {row.patientName}
                        </span>
                    </span>
                    {showNoOutreachToken ? (
                        <Badge
                            variant="outline"
                            className={TEMPLATE_TOKEN_CLASS}
                            {...dynamicWidgetBindingProps(
                                model.widgetId,
                                'live.rows.outreachState',
                            )}
                        >
                            no outreach yet
                        </Badge>
                    ) : null}
                </div>
                <p className="dark:text-muted-foreground truncate text-xs text-[#617578]">
                    {shiftRowMetaLine(
                        row,
                        model.widgetId,
                        rowIndex,
                        onCaregiverClick,
                    )}
                </p>
                {view.otherFieldIssues.map((issue) => (
                    <DynamicWidgetIssueNotice
                        key={issue.id}
                        issue={issue}
                        metricTags={metricTags}
                    />
                ))}
            </div>
            <div className="ml-auto flex min-w-0 flex-[1_1_16rem] flex-wrap items-center justify-end gap-2">
                {outreachPill !== null ? (
                    <StatusBadge
                        status={outreachPill}
                        {...dynamicWidgetBindingProps(
                            model.widgetId,
                            'live.rows.outreachState',
                        )}
                    />
                ) : view.statusDisplay !== null ? (
                    <StatusBadge
                        status={view.statusDisplay}
                        {...dynamicWidgetBindingProps(
                            model.widgetId,
                            `rows.${rowIndex}.status`,
                        )}
                    />
                ) : null}
                {view.statusIssues.map((issue) => (
                    <DynamicWidgetIssueNotice
                        key={issue.id}
                        issue={issue}
                        fieldLabel="Status unavailable"
                        metricTags={metricTags}
                    />
                ))}
                {canViewOutreach ? (
                    <button
                        type="button"
                        className={TEMPLATE_OUTLINE_BUTTON_XS_CLASS}
                        onClick={() => void onViewOutreach(row.shiftId)}
                    >
                        View outreach
                    </button>
                ) : null}
                {canStartOutreach && !canRequestOutreach ? (
                    <span
                        className="text-muted-foreground text-xs"
                        title={`One outreach supports up to ${START_OUTREACH_MAX_SHIFT_COUNT} shifts.`}
                    >
                        Split into groups of {START_OUTREACH_MAX_SHIFT_COUNT}
                    </span>
                ) : canRequestOutreach ? (
                    <div className="flex flex-col items-end gap-1">
                        <button
                            type="button"
                            className={TEMPLATE_OUTLINE_BUTTON_XS_CLASS}
                            {...dynamicWidgetBindingProps(
                                model.widgetId,
                                `rows.${rowIndex}.occurrenceIds`,
                            )}
                            disabled={actionButtonDisabled(
                                actionState.state,
                                actionState.isSubmitting,
                            )}
                            onClick={() =>
                                void onRequestOutreach(
                                    [row],
                                    `Create a coverage outreach for ${shiftReferences(
                                        row.occurrenceIds,
                                    )}`,
                                )
                            }
                        >
                            {actionState.isSubmitting ? (
                                <Spinner size="xs" />
                            ) : null}
                            {actionState.isSubmitting
                                ? 'Requesting outreach'
                                : actionButtonLabel(
                                      actionState.state,
                                      'Start outreach',
                                  )}
                        </button>
                        {actionIssue !== null ? (
                            <DynamicWidgetIssueNotice
                                issue={actionIssue}
                                metricTags={metricTags}
                            />
                        ) : null}
                        {view.actionIssues.map((issue) => (
                            <DynamicWidgetIssueNotice
                                key={issue.id}
                                issue={issue}
                                metricTags={metricTags}
                            />
                        ))}
                    </div>
                ) : null}
                {canAssign ? (
                    <button
                        type="button"
                        className={TEMPLATE_OUTLINE_BUTTON_XS_CLASS}
                        onClick={() =>
                            void promptAction(
                                `Assign a caregiver to ${row.patientName}’s shift${
                                    row.timeLabel ? ` (${row.timeLabel})` : ''
                                } — show me who is available.`,
                            )
                        }
                    >
                        Assign
                    </button>
                ) : null}
            </div>
        </div>
    );
}
